import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { TimeInput } from '@enfomentis/ui';
import { Time } from '@internationalized/date';
import type { TimeInputValue } from '@heroui/react';
import { Clock, Coffee, Calendar, Zap, Sun, Moon } from 'lucide-react';

export const TimeInputExamples: React.FC = () => {
  const [meetingTime, setMeetingTime] = useState<TimeInputValue | null>(new Time(14, 30));
  const [breakTime, setBreakTime] = useState<TimeInputValue | null>(null);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
      <div className="space-y-4">
        <h1 className="text-3xl font-bold">TimeInput Component</h1>
        <p className="text-lg text-muted-foreground">
          Professional time input with internationalization support and flexible formatting.
        </p>
      </div>

      {/* Basic Usage */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Basic Usage</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TimeInput 
              label="Default Time Input"
            />
            <TimeInput 
              label="With Default Value"
              defaultValue={new Time(9, 0)}
            />
            <TimeInput 
              label="Required Field"
              isRequired
            />
            <TimeInput 
              label="With Description"
              description="Select your preferred meeting time"
            />
          </div>
        </CardBody>
      </Card>

      {/* Size Variants */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Size Variants</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TimeInput 
              label="Small"
              size="sm"
              defaultValue={new Time(10, 15)}
            />
            <TimeInput 
              label="Medium (Default)"
              size="md"
              defaultValue={new Time(11, 30)}
            />
            <TimeInput 
              label="Large"
              size="lg"
              defaultValue={new Time(13, 45)}
            />
          </div>
        </CardBody>
      </Card>

      {/* Color Variants */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Color Variants</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TimeInput 
              label="Default"
              color="default"
              defaultValue={new Time(9, 0)}
            />
            <TimeInput 
              label="Primary"
              color="primary"
              defaultValue={new Time(10, 0)}
            />
            <TimeInput 
              label="Secondary"
              color="secondary"
              defaultValue={new Time(11, 0)}
            />
            <TimeInput 
              label="Success"
              color="success"
              defaultValue={new Time(12, 0)}
            />
            <TimeInput 
              label="Warning"
              color="warning"
              defaultValue={new Time(13, 0)}
            />
            <TimeInput 
              label="Danger"
              color="danger"
              defaultValue={new Time(14, 0)}
            />
          </div>
        </CardBody>
      </Card>

      {/* Time Format & Granularity */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sun className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Time Formats & Granularity</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TimeInput 
              label="12-hour Format"
              hourCycle={12}
              defaultValue={new Time(14, 30)}
            />
            <TimeInput 
              label="24-hour Format"
              hourCycle={24}
              defaultValue={new Time(14, 30)}
            />
            <TimeInput 
              label="Hour Only"
              granularity="hour"
              defaultValue={new Time(14)}
            />
            <TimeInput 
              label="Hour & Minute"
              granularity="minute"
              defaultValue={new Time(14, 30)}
            />
            <TimeInput 
              label="With Seconds"
              granularity="second"
              defaultValue={new Time(14, 30, 45)}
            />
          </div>
        </CardBody>
      </Card>

      {/* Validation & States */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Validation & States</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TimeInput 
              label="Disabled"
              isDisabled
              defaultValue={new Time(9, 0)}
            />
            <TimeInput 
              label="Read Only"
              isReadOnly
              defaultValue={new Time(17, 30)}
            />
            <TimeInput 
              label="With Time Range"
              minValue={new Time(9, 0)}
              maxValue={new Time(17, 0)}
              defaultValue={new Time(12, 0)}
              description="Business hours only (9 AM - 5 PM)"
            />
            <TimeInput 
              label="Invalid State"
              isInvalid
              errorMessage="Please select a valid time"
              defaultValue={new Time(8, 0)}
            />
          </div>
        </CardBody>
      </Card>

      {/* Controlled State */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Controlled State</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <TimeInput 
                label="Meeting Time"
                value={meetingTime}
                onChange={setMeetingTime}
                description="Selected time will update below"
              />
              <p className="text-sm text-muted-foreground">
                Selected: {meetingTime ? meetingTime.toString() : 'None'}
              </p>
            </div>
            <div className="space-y-2">
              <TimeInput 
                label="Break Time"
                value={breakTime}
                onChange={setBreakTime}
                description="Optional break time"
              />
              <p className="text-sm text-muted-foreground">
                Selected: {breakTime ? breakTime.toString() : 'None'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* With Icons & Content */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">With Start & End Content</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TimeInput 
              label="Coffee Break"
              startContent={<Coffee className="h-4 w-4 text-default-400" />}
              defaultValue={new Time(15, 30)}
            />
            <TimeInput 
              label="Event Time"
              startContent={<Calendar className="h-4 w-4 text-primary" />}
              defaultValue={new Time(19, 0)}
            />
            <TimeInput 
              label="Morning Shift"
              startContent={<Sun className="h-4 w-4 text-warning" />}
              defaultValue={new Time(6, 0)}
            />
            <TimeInput 
              label="Night Shift"
              startContent={<Moon className="h-4 w-4 text-secondary" />}
              defaultValue={new Time(22, 0)}
            />
          </div>
        </CardBody>
      </Card>

      {/* Trading Schedule Example */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Trading Schedule Example</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TimeInput 
                label="Market Open"
                defaultValue={new Time(9, 30)}
                hourCycle={12}
                color="success"
                description="NYSE opening time"
                isRequired
              />
              <TimeInput 
                label="Market Close"
                defaultValue={new Time(16, 0)}
                hourCycle={12}
                color="danger"
                description="NYSE closing time"
                isRequired
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TimeInput 
                label="Pre-market Start"
                defaultValue={new Time(4, 0)}
                hourCycle={12}
                color="warning"
                description="Extended hours trading"
              />
              <TimeInput 
                label="After-hours End"
                defaultValue={new Time(20, 0)}
                hourCycle={12}
                color="warning"
                description="Extended hours trading"
              />
            </div>
          </div>
        </CardBody>
      </Card>
      </div>
    </PlaygroundPageLayout>
  );
};